import DisplayText from '@sdk/components/ui/DisplayText';
import H3 from '@sdk/components/ui/typography/H3';
import Text16 from '@sdk/components/ui/typography/Text16';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Column from '@sdk/components/ui/spacing/Column';
import Button from '@sdk/components/ui/buttons/Button';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { SignupScreenContainer } from '@sdk/login/components/SignupScreenContainer';
import TextField from '@sdk/components/forms/TextField';
import Text12 from '@sdk/components/ui/typography/Text12';
import FieldError from '@sdk/components/forms/components/FieldError';

import { PointOfSale } from '@web/onboarding/types';
import CloverIcon from '@web/assets/svg/integrations/pos/clover.svg';
import HarbourTouchIcon from '@web/assets/svg/integrations/pos/harbourTouch.svg';
import LightSpeedIcon from '@web/assets/svg/integrations/pos/lightSpeed.svg';
import MicrosIcon from '@web/assets/svg/integrations/pos/micros.svg';
import NcrIcon from '@web/assets/svg/integrations/pos/ncr.svg';
import PositouchIcon from '@web/assets/svg/integrations/pos/positouch.svg';
import RestaurantManagerIcon from '@web/assets/svg/integrations/pos/restaurantManager.svg';
import RevelIcon from '@web/assets/svg/integrations/pos/revel.svg';
import SquareIcon from '@web/assets/svg/integrations/pos/square.svg';
import ToastIcon from '@web/assets/svg/integrations/pos/toast.svg';
import TouchBistroIcon from '@web/assets/svg/integrations/pos/touchBistro.svg';
import {
  ActionButtonContainer,
  POSActionsContainer,
  POSItem,
  POSSystemsContainer,
  POSSystemsScreenContainer,
  SkipSelectBtn,
} from '@web/onboarding/steps/components/PointOfSaleStep.styles';
import { usePointOfSale } from '@web/onboarding/hooks/usePointOfSale';
import FullPageContainer from '@web/components/FullPageContainer';

const icons: Record<PointOfSale, any> = {
  clover: CloverIcon,
  harborTouch: HarbourTouchIcon,
  lightSpeed: LightSpeedIcon,
  micros: MicrosIcon,
  ncr: NcrIcon,
  positouch: PositouchIcon,
  restaurantManager: RestaurantManagerIcon,
  revel: RevelIcon,
  square: SquareIcon,
  toast: ToastIcon,
  touchBistro: TouchBistroIcon,
  // eslint-disable-next-line react/display-name
  other: () => <DisplayText>{gettext('Other')}</DisplayText>,
  none: null,
};

type Props = {
  selected: PointOfSale | null;
  onSelect: (pos: PointOfSale) => void;
  onSubmit: (pos: PointOfSale | null, posOther: string | null) => void;
  onSkip: () => void;
};

export const PointOfSaleStep = ({ selected, onSelect, onSubmit, onSkip }: Props) => {
  const posSystems = Object.values(PointOfSale).filter((val) => val !== PointOfSale.none);
  const { form } = usePointOfSale({ selected, onSubmit });

  return (
    <FullPageContainer>
      <SignupScreenContainer>
        <POSSystemsScreenContainer>
          <div>
            <Column mBottom={40}>
              <H3 top={20} bottom={10}>
                {gettext('What POS do you use?')}
              </H3>
              <Text16 themeColor={ThemeColorType.gray}>
                {gettext('Specify your POS system to complete the signup.')}
              </Text16>

              <FieldError
                invalid={!!form.fields.pos.error}
                error={`* ${form.fields.pos.error?.toUpperCase()}`}
              />
            </Column>

            <POSSystemsContainer>
              {posSystems.map((item) => {
                const Icon = icons[item];
                return (
                  <POSItem key={item} isSelected={selected === item} onClick={() => onSelect(item)}>
                    <Icon />
                  </POSItem>
                );
              })}
            </POSSystemsContainer>

            {selected === PointOfSale.other && (
              <Column mTop={40}>
                <Column mBottom={5}>
                  <Text12 themeColor={ThemeColorType.gray}>
                    {gettext('Specify your POS system')}
                  </Text12>
                </Column>

                <TextField
                  invalid={!!form.fields.posName.error}
                  error={`* ${form.fields.posName.error?.toUpperCase()}`}
                  placeholder={gettext('Please specify...')}
                  value={form.fields.posName.value}
                  onChange={form.fields.posName.onChange}
                />
              </Column>
            )}

            <POSActionsContainer>
              <SkipSelectBtn themeColor={ThemeColorType.gray} fontSize={14} onClick={onSkip}>
                {gettext("We don't use POS")}
              </SkipSelectBtn>

              <ActionButtonContainer>
                <Button
                  buttonType={ButtonType.primary}
                  inactive={
                    !selected || (selected === PointOfSale.other && !form.fields.posName.value)
                  }
                  onClick={form.submit}>
                  {gettext('Next')}
                </Button>
              </ActionButtonContainer>
            </POSActionsContainer>
          </div>
        </POSSystemsScreenContainer>
      </SignupScreenContainer>
    </FullPageContainer>
  );
};
